/** LecturePage.tsx — 업무관리 「강의 스케줄」 (2026-09-28)
 *  동행상담과 같은 달력 화면: 월 달력(강의 칩) + 선택 날짜 카드 목록 → 누르면 상세/등록 전체 화면
 *  한 강의 = 일정 + 강의 대상자 + 요청자 + 강의파일 + 사전숙제 + 사후선물
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { C, T, R, IMEText, IMEArea, Btn, DDayBadge, SearchInput } from '../components/ui';
import type { TMStore } from '../hooks';
import * as api from '../api-lectures';
import { getHolidays } from '../api';
import { consultBusy, lectureBusy, findConflicts, clashIds, LECTURE_MIN } from '../scheduleConflict';
import type { Busy } from '../scheduleConflict';
import type { Consultation } from '../types';
import type { Lecture, LectureDraft, LectureFile, LectureFileKind, LectureStatus } from '../api-lectures';

interface Props {
  store: TMStore;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
  navParams?: { lectureId?: string };
  onNavigate?: (section: string, params?: unknown) => void;
}

type Follow = 'none' | 'homework' | 'gift';

const STATUS_META: Record<LectureStatus, { label: string; bg: string; color: string }> = {
  planned: { label: '예정', bg: C.orangeLt, color: C.orange },
  confirmed: { label: '확정', bg: C.accentLt, color: C.accent },
  done: { label: '완료', bg: C.greenLt, color: C.green },
  canceled: { label: '취소', bg: C.bgAlt, color: C.muted },
};
const AUDIENCE_PRESETS = ['FP', '신입 FP', '지점장·팀장', 'WM', '법인 대표', '세무·회계사', '기타'];

function fmtSize(n: number | null): string {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
const needsHomework = (l: Lecture) => l.status !== 'canceled' && !!l.homework_text.trim() && !l.homework_sent;
const needsGift = (l: Lecture) => l.status !== 'canceled' && !!l.gift_item.trim() && l.gift_status !== 'sent';

// ─────────────────────────────── 페이지 (동행상담과 같은 달력 화면) ───────────────────────────────
const DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const ymd = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const todayYmd = () => { const n = new Date(); return ymd(n.getFullYear(), n.getMonth(), n.getDate()); };
const CHIP_TONE: Record<LectureStatus, { bg: string; color: string; bar: string }> = {
  planned: { bg: '#FEF3C7', color: '#92400E', bar: '#D97706' },
  confirmed: { bg: '#EDE9FE', color: '#5B21B6', bar: '#7C3AED' },
  done: { bg: '#E5E7EB', color: '#6B7280', bar: '#9CA3AF' },
  canceled: { bg: '#F3F4F6', color: '#9CA3AF', bar: '#D1D5DB' },
};

export default function LecturePage({ store, onToast, navParams, onNavigate }: Props) {
  const [items, setItems] = useState<Lecture[]>([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selDate, setSelDate] = useState<string>(todayYmd());
  const [holidays, setHolidays] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');
  const [follow, setFollow] = useState<Follow>('none');
  const [view, setView] = useState<{ kind: 'cal' } | { kind: 'new'; date: string | null } | { kind: 'edit'; id: string }>({ kind: 'cal' });
  const toast = useCallback((m: string, k?: 'success' | 'error') => onToast?.(m, k), [onToast]);
  const dirtyRef = useRef(false);
  const guard = (go: () => void) => { if (dirtyRef.current && !window.confirm('저장하지 않은 내용이 있습니다. 그래도 나갈까요?')) return; dirtyRef.current = false; go(); };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getLectures({ period: 'all' });
      if (r.success && r.data) setItems(r.data);
      else toast(r.error || '강의 목록을 불러오지 못했습니다', 'error');
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { reload(); }, [reload]);
  // 동행상담 달력에서 강의 칩을 눌러 들어온 경우 — 그 강의를 바로 연다
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    const id = navParams?.lectureId;
    if (!id || openedRef.current === id || loading) return;
    const l = items.find(x => x.id === id);
    if (l) { openedRef.current = id; if (l.lecture_date) { const [y, m] = l.lecture_date.split('-').map(Number); setYear(y); setMonth(m - 1); setSelDate(l.lecture_date); } setView({ kind: 'edit', id }); }
  }, [navParams, items, loading]);
  const consultByDate = useMemo(() => {
    const m: Record<string, Consultation[]> = {};
    store.consultations.forEach(c => {
      const dates = c.confirm_status === 'tentative' && c.candidate_slots?.length ? c.candidate_slots.map(sl => String(sl.date || '').slice(0, 10)) : [String(c.date || '').slice(0, 10)];
      Array.from(new Set(dates)).forEach(d => { if (d) (m[d] = m[d] || []).push(c); });
    });
    return m;
  }, [store.consultations]);
  const busyAll: Busy[] = useMemo(() => [...consultBusy(store.consultations), ...lectureBusy(items)], [store.consultations, items]);
  const clash = useMemo(() => clashIds(busyAll), [busyAll]);
  useEffect(() => {
    getHolidays(year).then(res => {
      const map: Record<string, string> = {};
      (res.data || []).forEach(h => { map[String(h.date).slice(0, 10)] = h.name; });
      setHolidays(map);
    }).catch(() => {});
  }, [year]);

  const kw = q.trim().toLowerCase();
  const visible = useMemo(() => items.filter(l => {
    if (follow === 'homework' && !needsHomework(l)) return false;
    if (follow === 'gift' && !needsGift(l)) return false;
    if (!kw) return true;
    return [l.title, l.requester_name, l.requester_org, l.audience_detail, l.place, l.audience_types.join(' ')].join(' ').toLowerCase().includes(kw);
  }), [items, follow, kw]);

  const byDate = useMemo(() => {
    const m: Record<string, Lecture[]> = {};
    visible.forEach(l => { if (l.lecture_date) (m[l.lecture_date] = m[l.lecture_date] || []).push(l); });
    Object.values(m).forEach(arr => arr.sort((a, b) => (a.start_time || '99').localeCompare(b.start_time || '99')));
    return m;
  }, [visible]);
  const undated = visible.filter(l => !l.lecture_date);

  const calDays = useMemo(() => {
    const first = new Date(year, month, 1).getDay();
    const last = new Date(year, month + 1, 0).getDate();
    const out: (null | { day: number; date: string; dow: number })[] = [];
    for (let i = 0; i < first; i++) out.push(null);
    for (let d = 1; d <= last; d++) out.push({ day: d, date: ymd(year, month, d), dow: new Date(year, month, d).getDay() });
    while (out.length % 7) out.push(null);
    return out;
  }, [year, month]);

  const counts = useMemo(() => ({
    month: items.filter(l => l.lecture_date && l.lecture_date.slice(0, 7) === ymd(year, month, 1).slice(0, 7) && l.status !== 'canceled').length,
    homework: items.filter(needsHomework).length,
    gift: items.filter(needsGift).length,
  }), [items, year, month]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const goToday = () => { const n = new Date(); setYear(n.getFullYear()); setMonth(n.getMonth()); setSelDate(todayYmd()); };
  const jumpTo = (d: string | null) => { if (!d) return; const [y, m] = d.split('-').map(Number); setYear(y); setMonth(m - 1); setSelDate(d); };

  const onSaved = (l: Lecture, isNew: boolean) => {
    setItems(prev => (isNew ? [...prev, l] : prev.map(x => (x.id === l.id ? l : x))));
    dirtyRef.current = false;
    jumpTo(l.lecture_date);
    setView({ kind: 'edit', id: l.id });
    toast(isNew ? '강의를 등록했습니다' : '저장했습니다', 'success');
  };
  const onDeleted = (id: string) => { setItems(prev => prev.filter(x => x.id !== id)); dirtyRef.current = false; setView({ kind: 'cal' }); toast('삭제했습니다', 'success'); };
  const back = () => guard(() => setView({ kind: 'cal' }));

  // ── 등록 · 상세 (동행상담처럼 달력 대신 전체 화면으로) ──
  const sel = view.kind === 'edit' ? items.find(x => x.id === view.id) || null : null;
  const initial = view.kind === 'new' ? { ...api.emptyLecture(), lecture_date: view.date } : sel;
  if (view.kind !== 'cal' && initial) {   // 지워진 강의를 가리키면 달력을 그대로 보여준다
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <LectureEditor key={view.kind === 'new' ? 'new' : view.id + (sel?.updated_at || '')} id={view.kind === 'edit' ? view.id : undefined}
          initial={initial} store={store} onSaved={onSaved} onDeleted={onDeleted} onBack={back}
          onDirty={d => { dirtyRef.current = d; }} toast={toast} busy={busyAll} />
      </div>
    );
  }

  const selList = byDate[selDate] || [];
  const selDow = DAYS[new Date(selDate + 'T00:00:00').getDay()];
  const filterPill = (key: Follow, label: string, n: number, color: string) => (
    <button onClick={() => setFollow(follow === key ? 'none' : key)} style={{
      padding: '6px 12px', borderRadius: 8, fontSize: T.sm, cursor: 'pointer', fontWeight: follow === key ? 800 : 600,
      border: `1px solid ${follow === key ? color : C.border}`, background: follow === key ? `${color}14` : C.white, color: follow === key ? color : C.sub,
    }}>{label} <b style={{ color }}>{n}</b></button>
  );

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      {/* 헤더 — 동행상담과 같은 배치 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={prevMonth} style={navBtn}>◀</button>
        <span style={{ fontSize: T.xl, fontWeight: 800, minWidth: 110, textAlign: 'center' }}>{year}년 {month + 1}월</span>
        <button onClick={nextMonth} style={navBtn}>▶</button>
        <button onClick={goToday} style={{ ...navBtn, fontSize: T.sm }}>오늘</button>
        <span style={{ fontSize: T.sm, color: C.sub, marginLeft: 4 }}>이번 달 강의 <b style={{ color: C.purple }}>{counts.month}</b>건</span>
        <span style={{ display: 'flex', gap: 10, fontSize: T.xs, color: C.sub, marginLeft: 8 }}><span><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#7C3AED', marginRight: 4, verticalAlign: -1 }} />🎤 강의</span><span><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#16A34A', marginRight: 4, verticalAlign: -1 }} />🤝 상담</span><span style={{ color: '#DC2626', fontWeight: 700 }}>⚠ 시간 겹침</span></span>
        <div style={{ flex: 1 }} />
        {filterPill('homework', '📝 숙제 미발송', counts.homework, C.orange)}
        {filterPill('gift', '🎁 선물 미발송', counts.gift, C.purple)}
        <div style={{ minWidth: 140, width: 240 }}><SearchInput value={q} onChange={setQ} placeholder="주제·요청자·대상" /></div>
        <Btn onClick={() => setView({ kind: 'new', date: selDate })} style={{ fontSize: T.md, padding: '8px 16px' }}>+ 강의 등록</Btn>
      </div>

      {/* 달력 */}
      <div style={{ borderRadius: 12, background: C.white, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 16, opacity: loading ? 0.6 : 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {DAYS.map((d, i) => (
            <div key={d} style={{ textAlign: 'center', fontSize: T.sm, fontWeight: 700, padding: '8px 4px', background: C.bgAlt, borderBottom: `1px solid ${C.border}`, color: i === 0 ? '#DC2626' : i === 6 ? '#2563EB' : C.sub }}>{d}</div>
          ))}
          {calDays.map((d, i) => {
            if (!d) return <div key={`e${i}`} style={{ borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, minHeight: 92 }} />;
            const list = byDate[d.date] || [];
            const isToday = d.date === todayYmd();
            const isSel = d.date === selDate;
            const hol = holidays[d.date];
            const red = d.dow === 0 || !!hol; const blue = d.dow === 6;
            return (
              <div key={d.date} onClick={() => setSelDate(d.date)} onDoubleClick={() => setView({ kind: 'new', date: d.date })} title="더블클릭하면 이 날짜로 강의 등록"
                style={{
                  borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, minHeight: 92, padding: '4px 5px', cursor: 'pointer',
                  background: isSel ? '#EDE9FE' : isToday ? '#F0FDF4' : red ? '#FEF2F2' : blue ? '#EFF6FF' : 'transparent',
                  outline: isSel ? `2px solid ${C.purple}` : 'none', outlineOffset: '-2px',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                  <span style={{ fontSize: T.sm, fontWeight: isToday ? 800 : 600, background: isToday ? '#16A34A' : 'transparent', color: isToday ? '#fff' : red ? '#DC2626' : blue ? '#2563EB' : C.text, width: isToday ? 22 : undefined, height: isToday ? 22 : undefined, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{d.day}</span>
                  {list.length > 0 && <span style={{ fontSize: T.xs, fontWeight: 700, padding: '0 6px', borderRadius: 999, background: C.purple + '22', color: C.purple }}>{list.length}</span>}
                </div>
                {hol && <div style={{ fontSize: T.xs, color: '#DC2626', fontWeight: 700, marginBottom: 1, lineHeight: 1.2 }}>{hol}</div>}
                {list.slice(0, 2).map(l => {
                  const t = CHIP_TONE[l.status];
                  return (
                    <div key={l.id} onClick={e => { e.stopPropagation(); setSelDate(d.date); setView({ kind: 'edit', id: l.id }); }}
                      style={{
                        fontSize: T.xs, lineHeight: 1.3, padding: '2px 4px', borderRadius: 3, marginBottom: 1, cursor: 'pointer',
                        background: t.bg, color: t.color, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        textDecoration: l.status === 'canceled' ? 'line-through' : 'none',
                        boxShadow: clash.has(`lecture:${l.id}`) ? 'inset 0 0 0 1px #DC2626' : 'none',
                      }}>
                      {clash.has(`lecture:${l.id}`) ? '⚠ ' : ''}🎤 {l.start_time} {l.requester_org || l.audience_types[0] || ''} {l.title}
                      {(needsHomework(l) || needsGift(l)) && <span style={{ marginLeft: 3 }}>{needsHomework(l) ? '📝' : ''}{needsGift(l) ? '🎁' : ''}</span>}
                    </div>
                  );
                })}
                {list.length > 2 && <div style={{ fontSize: 10, color: C.muted, textAlign: 'center' }}>+{list.length - 2}</div>}
                {(consultByDate[d.date] || []).slice(0, 2).map(c => (
                  <div key={'C' + c.id} onClick={e => { e.stopPropagation(); onNavigate?.('consultation'); }} title={`🤝 동행상담 · ${c.company_name || ''}`}
                    style={{ fontSize: T.xs, lineHeight: 1.3, padding: '2px 4px', borderRadius: 3, marginBottom: 1, cursor: 'pointer', background: '#DCFCE7', color: '#166534', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', boxShadow: clash.has(`consult:${c.id}`) ? 'inset 0 0 0 1px #DC2626' : 'none' }}>
                    {clash.has(`consult:${c.id}`) ? '⚠ ' : ''}🤝 {(c.time || '').slice(0, 5)} {c.company_name || '상담'}
                  </div>
                ))}
                {(consultByDate[d.date]?.length || 0) > 2 && <div style={{ fontSize: 10, color: '#16A34A', textAlign: 'center' }}>상담 +{(consultByDate[d.date]?.length || 0) - 2}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* 선택 날짜 상세 — 동행상담과 같은 카드 목록 */}
      <div style={{ borderRadius: 12, background: C.white, border: `1px solid ${C.border}`, padding: 16, marginBottom: undated.length ? 16 : 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <span style={{ fontSize: T.lg, fontWeight: 700 }}>{selDate}</span>
            <span style={{ fontSize: T.md, color: C.muted, marginLeft: 8 }}>({selDow})</span>
            {holidays[selDate] && <span style={{ fontSize: T.sm, color: '#DC2626', fontWeight: 600, marginLeft: 8 }}>{holidays[selDate]}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: T.md, color: C.sub }}>강의 {selList.length}건{consultByDate[selDate]?.length ? ` · 상담 ${consultByDate[selDate].length}건` : ''}</span>
            <Btn variant="secondary" onClick={() => setView({ kind: 'new', date: selDate })} style={{ fontSize: T.sm, padding: '6px 12px' }}>+ 이 날짜에 등록</Btn>
          </div>
        </div>
        {selList.map(l => <DayCard key={l.id} l={l} clash={clash.has(`lecture:${l.id}`)} onClick={() => setView({ kind: 'edit', id: l.id })} />)}
        {(consultByDate[selDate] || []).map(c => (
          <div key={'C' + c.id} onClick={() => onNavigate?.('consultation')} style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 8, background: '#F0FDF4', border: `1px solid ${clash.has(`consult:${c.id}`) ? '#FCA5A5' : '#BBF7D0'}`, cursor: 'pointer', fontSize: T.sm, color: C.sub }}>
            <b style={{ color: C.text }}>🤝 동행상담 · {c.company_name || '업체'}</b> · {(c.time || '').slice(0, 5) || '시간 미정'}{c.fp_name ? ` · ${c.fp_name}` : ''}{c.location ? ` · 📍${c.location}` : ''}
            {clash.has(`consult:${c.id}`) && <span style={{ color: '#DC2626', fontWeight: 700 }}> · ⚠ 강의와 시간이 겹칩니다</span>}
          </div>
        ))}
        {selList.length === 0 && !(consultByDate[selDate]?.length) && <div style={{ textAlign: 'center', padding: '24px 0', color: C.muted, fontSize: T.md }}>해당 날짜에 일정이 없습니다</div>}
      </div>

      {undated.length > 0 && (
        <div style={{ borderRadius: 12, background: C.white, border: `1px dashed ${C.borderDk}`, padding: 16 }}>
          <div style={{ fontSize: T.md, fontWeight: 700, marginBottom: 10 }}>날짜 미정 <span style={{ color: C.muted, fontWeight: 500 }}>{undated.length}건</span></div>
          {undated.map(l => <DayCard key={l.id} l={l} onClick={() => setView({ kind: 'edit', id: l.id })} />)}
        </div>
      )}
    </div>
  );
}

const navBtn: React.CSSProperties = { padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.md };

function DayCard({ l, onClick, clash }: { l: Lecture; onClick: () => void; clash?: boolean }) {
  const st = STATUS_META[l.status];
  const nLec = l.files.filter(f => f.kind === 'lecture').length;
  const time = [l.start_time, l.end_time].filter(Boolean).join(' ~ ') || '시간 미정';
  return (
    <div onClick={onClick} style={{ padding: '12px 14px', borderRadius: 10, marginBottom: 8, background: C.bg, border: `1px solid ${clash ? '#FCA5A5' : C.border}`, cursor: 'pointer' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 8 }}>
        <span style={{ fontSize: T.md, fontWeight: 700, textDecoration: l.status === 'canceled' ? 'line-through' : 'none' }}>🎤 {l.title}</span>
        <span style={{ fontSize: T.xs, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.color, flexShrink: 0 }}>{st.label}</span>
      </div>
      <div style={{ fontSize: T.sm, color: C.muted, marginBottom: 6 }}>
        {time} · 📍{l.place || (l.mode === 'online' ? '온라인' : '장소 미정')}
        {' · '}👥 {[l.audience_types.join('·'), l.headcount ? `${l.headcount}명` : ''].filter(Boolean).join(' ') || '대상 미입력'}
        {(l.requester_name || l.requester_org) && <> · 🙋 {[l.requester_org, l.requester_name].filter(Boolean).join(' ')}</>}
        {l.creator_name && <> · ✍ {l.creator_name}</>}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {nLec > 0 && <Chip bg={C.white} color={C.sub}>📎 강의파일 {nLec}</Chip>}
        {needsHomework(l) && <Chip bg={C.orangeLt} color={C.orange}>📝 숙제 미발송{l.homework_due ? ` · 마감 ${l.homework_due.slice(5).replace('-', '.')}` : ''}</Chip>}
        {l.homework_text.trim() && l.homework_sent && <Chip bg={C.greenLt} color={C.green}>📝 숙제 발송</Chip>}
        {needsGift(l) && <Chip bg={C.purpleLt} color={C.purple}>🎁 {l.gift_item} {l.gift_status === 'prepare' ? '준비중' : '미정'}</Chip>}
        {l.gift_status === 'sent' && <Chip bg={C.greenLt} color={C.green}>🎁 선물 발송</Chip>}
        {clash && <Chip bg={C.redLt} color={C.red}>⚠ 다른 일정과 시간 겹침</Chip>}
      </div>
    </div>
  );
}

function Chip({ children, bg, color }: { children: React.ReactNode; bg: string; color: string }) {
  return <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: bg, color }}>{children}</span>;
}

// ─────────────────────────────── 편집기 ───────────────────────────────
interface EditorProps {
  id?: string;
  initial: LectureDraft;
  store: TMStore;
  onSaved: (l: Lecture, isNew: boolean) => void;
  onDeleted?: (id: string) => void;
  onCancel?: () => void;
  onBack?: () => void;
  onDirty?: (dirty: boolean) => void;
  busy?: Busy[];
  toast: (m: string, k?: 'success' | 'error') => void;
}

function LectureEditor({ id, initial, store, onSaved, onDeleted, onCancel, onBack, onDirty, busy, toast }: EditorProps) {
  const [f, setF] = useState<LectureDraft>(() => ({ ...api.emptyLecture(), ...initial }));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const isNew = !id;
  const conflicts = useMemo(() => findConflicts(busy || [], f.lecture_date, f.start_time, f.end_time, LECTURE_MIN, { kind: 'lecture', id }), [busy, f.lecture_date, f.start_time, f.end_time, id]);
  useEffect(() => { onDirty?.(dirty); }, [dirty, onDirty]);
  const set = <K extends keyof LectureDraft>(k: K, v: LectureDraft[K]) => { setF(p => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!f.title.trim()) { toast('강의주제를 입력해 주세요', 'error'); return; }
    if (f.status !== 'canceled' && conflicts.overlaps.length > 0) {
      const list = conflicts.overlaps.map(b => `· ${b.kind === 'lecture' ? '🎤 강의' : '🤝 상담'} ${b.time} ${b.label}`).join('\n');
      if (!window.confirm(`시간이 겹치는 일정이 있습니다.\n\n${list}\n\n그래도 저장할까요?`)) return;
    }
    setSaving(true);
    try {
      const r = isNew ? await api.createLecture(f) : await api.updateLecture(id as string, f);
      if (r.success && r.data) { setDirty(false); onSaved(r.data, isNew); }
      else toast(r.error || '저장 실패', 'error');
    } catch (e) { toast(e instanceof Error ? e.message : '저장 실패', 'error'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!id) return;
    if (!window.confirm(`「${f.title}」 강의를 삭제할까요? 첨부파일도 함께 지워집니다.`)) return;
    const r = await api.deleteLecture(id);
    if (r.success) onDeleted?.(id); else toast(r.error || '삭제 실패', 'error');
  };

  // Ctrl/⌘+S 저장
  const saveRef = useRef(save); saveRef.current = save;
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); saveRef.current(); } };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, []);

  // 요청자 — FP/WM 목록에서 고르면 이름·소속·연락처를 채운다(이후 자유 수정)
  const pickRequester = (v: string) => {
    if (!v) { setF(p => ({ ...p, requester_kind: 'etc', requester_ref_id: null })); setDirty(true); return; }
    const [kind, rid] = v.split(':');
    if (kind === 'fp') {
      const x = store.fps.find(p => p.id === rid);
      if (x) setF(p => ({ ...p, requester_kind: 'fp', requester_ref_id: x.id, requester_name: x.name, requester_org: x.company || '', requester_phone: x.phone || '' }));
    } else if (kind === 'wm') {
      const x = store.wms.find(p => p.id === rid);
      if (x) setF(p => ({ ...p, requester_kind: 'wm', requester_ref_id: x.id, requester_name: x.name, requester_org: x.company || '', requester_phone: x.phone || '' }));
    }
    setDirty(true);
  };
  const reqValue = f.requester_ref_id && f.requester_kind !== 'etc' ? `${f.requester_kind}:${f.requester_ref_id}` : '';

  const filesOf = (k: LectureFileKind) => f.files.filter(x => x.kind === k);
  const setFilesOf = (k: LectureFileKind, next: LectureFile[]) => set('files', [...f.files.filter(x => x.kind !== k), ...next]);

  const toggleAud = (a: string) => set('audience_types', f.audience_types.includes(a) ? f.audience_types.filter(x => x !== a) : [...f.audience_types, a]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>
      {/* 상단 바 */}
      <div style={{ padding: '12px 20px', background: C.white, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
        {onBack && <button onClick={onBack} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.sm, color: C.sub, flexShrink: 0 }}>← 달력</button>}
        <span style={{ fontSize: T.xl, fontWeight: 800, color: C.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isNew ? '새 강의 등록' : (f.title || '제목 없음')}
        </span>
        {dirty && <span style={{ fontSize: T.xs, color: C.orange, fontWeight: 700 }}>● 저장 안 됨</span>}
        {!isNew && <Btn variant="danger" onClick={remove}>삭제</Btn>}
        {isNew && onCancel && <Btn variant="secondary" onClick={onCancel}>취소</Btn>}
        <Btn onClick={save} disabled={saving}>{saving ? '저장 중…' : isNew ? '등록' : '저장'}</Btn>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 40px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* 1. 기본 정보 */}
          <Sec icon="🎤" title="강의 정보">
            <Row>
              <In label="강의주제" required grow={3}><TextIn value={f.title} onChange={v => set('title', v)} placeholder="예) 정부지원금·정책자금으로 고객 터치하는 방법" /></In>
              <In label="상태" grow={1}>
                <Seg value={f.status} options={(Object.keys(STATUS_META) as LectureStatus[]).map(s => ({ v: s, label: STATUS_META[s].label, color: STATUS_META[s].color }))} onChange={v => set('status', v as LectureStatus)} />
              </In>
            </Row>
            <In label="한 줄 소개"><TextIn value={f.summary} onChange={v => set('summary', v)} placeholder="강의 목표 · 핵심 내용" /></In>
            <Row>
              <In label="날짜" grow={1.2}><DateIn value={f.lecture_date} onChange={v => set('lecture_date', v)} /></In>
              <In label="시작" grow={0.8}><TimeIn value={f.start_time} onChange={v => set('start_time', v)} /></In>
              <In label="종료" grow={0.8}><TimeIn value={f.end_time} onChange={v => set('end_time', v)} /></In>
              <In label="형태" grow={1.6}>
                <Seg value={f.mode} options={[{ v: 'offline', label: '대면' }, { v: 'online', label: '온라인' }, { v: 'hybrid', label: '병행' }]} onChange={v => set('mode', v as LectureDraft['mode'])} />
              </In>
            </Row>
            {f.status !== 'canceled' && conflicts.overlaps.length > 0 && (
              <div style={{ margin: '-4px 0 12px', padding: '10px 12px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FCA5A5', fontSize: T.sm, color: '#991B1B', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 800 }}>⚠ 이 시간에 다른 일정이 있습니다</div>
                {conflicts.overlaps.map(b => <div key={b.kind + b.id + b.time}>{b.kind === 'lecture' ? '🎤 강의' : '🤝 동행상담'} {b.time} · {b.label}</div>)}
              </div>
            )}
            {f.status !== 'canceled' && conflicts.overlaps.length === 0 && f.lecture_date && !f.start_time && conflicts.untimed.length > 0 && (
              <div style={{ margin: '-4px 0 12px', fontSize: T.xs, color: C.orange, fontWeight: 700 }}>이 날 다른 일정 {conflicts.untimed.length}건 — 시작 시간을 넣으면 겹침을 확인합니다</div>
            )}
            <Row>
              <In label={f.mode === 'online' ? '접속 링크' : '장소'} grow={3}><TextIn value={f.place} onChange={v => set('place', v)} placeholder={f.mode === 'online' ? 'Zoom · 웹엑스 링크' : '예) 한화생명 강남지점 3층 교육장'} /></In>
              <In label="강의료 (만원)" grow={1}><NumIn value={f.fee_manwon} onChange={v => set('fee_manwon', v)} placeholder="0" /></In>
            </Row>
          </Sec>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {/* 2. 강의 대상자 */}
            <Sec icon="👥" title="강의 대상자">
              <In label="대상 구분 (여러 개 선택)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {[...AUDIENCE_PRESETS, ...f.audience_types.filter(a => !AUDIENCE_PRESETS.includes(a))].map(a => {
                    const on = f.audience_types.includes(a);
                    return <button key={a} onClick={() => toggleAud(a)} style={{
                      padding: '5px 11px', borderRadius: 999, fontSize: T.sm, cursor: 'pointer', fontWeight: on ? 700 : 500,
                      border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accentLt : C.white, color: on ? C.accent : C.sub,
                    }}>{on ? '✓ ' : ''}{a}</button>;
                  })}
                  <button onClick={() => { const v = (window.prompt('추가할 대상 구분 (예: 보험대리점 대표)') || '').trim(); if (v && !f.audience_types.includes(v)) set('audience_types', [...f.audience_types, v]); }}
                    style={{ padding: '5px 11px', borderRadius: 999, fontSize: T.sm, cursor: 'pointer', border: `1px dashed ${C.borderDk}`, background: C.white, color: C.muted }}>+ 직접 입력</button>
                </div>
              </In>
              <Row>
                <In label="인원" grow={1}><NumIn value={f.headcount} onChange={v => set('headcount', v)} placeholder="명" /></In>
                <In label="대상 상세" grow={3}><TextIn value={f.audience_detail} onChange={v => set('audience_detail', v)} placeholder="예) 50·60대 FP 중심, 법인 고객 보유" /></In>
              </Row>
            </Sec>

            {/* 3. 요청자 */}
            <Sec icon="🙋" title="요청자">
              <In label="FP · WM 목록에서 선택 (선택)">
                <select value={reqValue} onChange={e => pickRequester(e.target.value)} style={inputStyle}>
                  <option value="">직접 입력</option>
                  {store.fps.length > 0 && <optgroup label="FP">{store.fps.map(p => <option key={p.id} value={`fp:${p.id}`}>{p.name}{p.company ? ` · ${p.company}` : ''}</option>)}</optgroup>}
                  {store.wms.length > 0 && <optgroup label="WM">{store.wms.map(p => <option key={p.id} value={`wm:${p.id}`}>{p.name}{p.company ? ` · ${p.company}` : ''}</option>)}</optgroup>}
                </select>
              </In>
              <Row>
                <In label="이름" grow={1}><TextIn value={f.requester_name} onChange={v => set('requester_name', v)} placeholder="김교육 지점장" /></In>
                <In label="소속" grow={1.3}><TextIn value={f.requester_org} onChange={v => set('requester_org', v)} placeholder="한화생명 강남지점" /></In>
              </Row>
              <In label="연락처"><TextIn value={f.requester_phone} onChange={v => set('requester_phone', v)} placeholder="010-0000-0000" /></In>
            </Sec>
          </div>

          {/* 4. 강의파일 */}
          <Sec icon="📎" title="강의파일" hint="강의안 · 배포자료 · 실습지">
            <FileBox kind="lecture" files={filesOf('lecture')} onChange={n => setFilesOf('lecture', n)} toast={toast} />
          </Sec>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {/* 5. 사전숙제 */}
            <Sec icon="📝" title="사전숙제" right={f.homework_text.trim() ? <Check label="수강생에게 발송 완료" value={f.homework_sent} onChange={v => set('homework_sent', v)} /> : null}>
              <In label="숙제 내용"><AreaIn value={f.homework_text} onChange={v => set('homework_text', v)} placeholder="예) 담당 법인 고객 1곳의 가결산 자료를 준비해 오세요" /></In>
              <Row>
                <In label="제출 마감" grow={1}><DateIn value={f.homework_due} onChange={v => set('homework_due', v)} /></In>
                <div style={{ flex: 1.4, display: 'flex', alignItems: 'flex-end', paddingBottom: 12 }}>
                  {f.homework_due && !f.homework_sent && f.homework_text.trim() && <span style={{ fontSize: T.xs, color: C.orange, fontWeight: 700 }}>마감 <DDayBadge deadline={f.homework_due} /> · 아직 발송 전</span>}
                </div>
              </Row>
              <FileBox kind="homework" files={filesOf('homework')} onChange={n => setFilesOf('homework', n)} toast={toast} compact />
            </Sec>

            {/* 6. 사후선물 */}
            <Sec icon="🎁" title="사후선물">
              <Row>
                <In label="선물" grow={2.2}><TextIn value={f.gift_item} onChange={v => set('gift_item', v)} placeholder="예) 강의자료 요약 책자 · 커피 쿠폰" /></In>
                <In label="수량" grow={0.8}><NumIn value={f.gift_qty} onChange={v => set('gift_qty', v)} placeholder={f.headcount ? String(f.headcount) : '개'} /></In>
              </Row>
              <In label="진행">
                <Seg value={f.gift_status} options={[{ v: 'none', label: '미정' }, { v: 'prepare', label: '준비중', color: C.purple }, { v: 'sent', label: '발송 완료', color: C.green }]} onChange={v => set('gift_status', v as LectureDraft['gift_status'])} />
              </In>
              <In label="메모"><TextIn value={f.gift_memo} onChange={v => set('gift_memo', v)} placeholder="발송 방법 · 주소 · 예산" /></In>
              <FileBox kind="gift" files={filesOf('gift')} onChange={n => setFilesOf('gift', n)} toast={toast} compact />
            </Sec>
          </div>

          {/* 7. 메모 */}
          <Sec icon="🗒" title="메모">
            <AreaIn value={f.memo} onChange={v => set('memo', v)} placeholder="강의 후기 · 질문 · 다음 강의 아이디어" rows={4} />
          </Sec>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────── 작은 부품 ───────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%', height: 38, padding: '8px 11px', borderRadius: 8, border: `1px solid ${C.border}`,
  fontSize: T.md, outline: 'none', boxSizing: 'border-box', background: C.white, color: C.text, fontFamily: 'inherit',
};

function Sec({ icon, title, hint, right, children }: { icon: string; title: string; hint?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: 'hidden' }}>
      <div style={{ padding: '11px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{icon}</span><span style={{ fontSize: T.md, fontWeight: 800, color: C.text }}>{title}</span>
        {hint && <span style={{ fontSize: T.xs, color: C.muted }}>{hint}</span>}
        <span style={{ flex: 1 }} />{right}
      </div>
      <div style={{ padding: '14px 16px 4px' }}>{children}</div>
    </div>
  );
}
function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>{children}</div>;
}
function In({ label, required, grow = 1, children }: { label: string; required?: boolean; grow?: number; children: React.ReactNode }) {
  return (
    <div style={{ flex: grow, minWidth: 0, marginBottom: 12 }}>
      <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 4 }}>{label}{required && <span style={{ color: C.red }}> *</span>}</div>
      {children}
    </div>
  );
}
function TextIn({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return <IMEText value={value} onChange={onChange} placeholder={placeholder} style={inputStyle} />;
}
function AreaIn({ value, onChange, placeholder, rows = 3 }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return <IMEArea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{ ...inputStyle, height: 'auto', resize: 'vertical', lineHeight: 1.6, marginBottom: 10 }} />;
}
function NumIn({ value, onChange, placeholder }: { value: number | null; onChange: (v: number | null) => void; placeholder?: string }) {
  return <input inputMode="numeric" value={value == null ? '' : String(value)} placeholder={placeholder}
    onChange={e => { const s = e.target.value.replace(/[^\d]/g, ''); onChange(s === '' ? null : Number(s)); }} style={inputStyle} />;
}
function DateIn({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  return <input type="date" value={value || ''} onChange={e => onChange(e.target.value || null)} style={inputStyle} />;
}
function TimeIn({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <input type="time" step={600} value={value} onChange={e => onChange(e.target.value.slice(0, 5))} style={inputStyle} />;
}
function Seg({ value, options, onChange }: { value: string; options: { v: string; label: string; color?: string }[]; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', height: 38 }}>
      {options.map((o, i) => {
        const on = o.v === value;
        return <button key={o.v} onClick={() => onChange(o.v)} style={{
          flex: 1, border: 'none', borderLeft: i ? `1px solid ${C.border}` : 'none', cursor: 'pointer', fontSize: T.sm,
          fontWeight: on ? 800 : 500, background: on ? (o.color ? `${o.color}14` : C.accentLt) : C.white, color: on ? (o.color || C.accent) : C.sub,
          whiteSpace: 'nowrap', padding: '0 6px',
        }}>{o.label}</button>;
      })}
    </div>
  );
}
function Check({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: T.sm, fontWeight: 700, color: value ? C.green : C.sub, cursor: 'pointer' }}>
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} />{label}
    </label>
  );
}

const FILE_ICON = (n: string) => {
  const e = (n.split('.').pop() || '').toLowerCase();
  if (['ppt', 'pptx', 'key'].includes(e)) return '📽';
  if (e === 'pdf') return '📄';
  if (['doc', 'docx', 'hwp', 'hwpx'].includes(e)) return '📝';
  if (['xls', 'xlsx', 'csv'].includes(e)) return '📊';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(e)) return '🖼';
  if (['mp4', 'mov', 'avi'].includes(e)) return '🎬';
  return '📎';
};

function FileBox({ kind, files, onChange, toast, compact }: { kind: LectureFileKind; files: LectureFile[]; onChange: (n: LectureFile[]) => void; toast: (m: string, k?: 'success' | 'error') => void; compact?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  const [over, setOver] = useState(false);
  const filesRef = useRef(files); filesRef.current = files;

  const add = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const arr = Array.from(list);
    setBusy(arr.length);
    const added: LectureFile[] = [];
    for (const file of arr) {
      if (file.size > 20 * 1024 * 1024) { toast(`${file.name} — 20MB 이하만 올릴 수 있습니다`, 'error'); setBusy(b => b - 1); continue; }
      const r = await api.uploadLectureFile(file, kind);
      if (r.success && r.data) added.push(r.data); else toast(r.error || `${file.name} 업로드 실패`, 'error');
      setBusy(b => b - 1);
    }
    if (added.length) { onChange([...filesRef.current, ...added]); toast(`${added.length}개 파일을 붙였습니다 — 저장하면 반영됩니다`, 'success'); }
    if (ref.current) ref.current.value = '';
  };
  const open = async (x: LectureFile) => {
    const r = await api.getLectureFileUrl(x);
    if (r.success && r.data?.url) window.open(r.data.url, '_blank'); else toast(r.error || '다운로드 링크 실패', 'error');
  };

  return (
    <div style={{ marginBottom: 12 }}>
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {files.map(x => (
            <div key={x.s3_key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.bgAlt }}>
              <span>{FILE_ICON(x.name)}</span>
              <span onClick={() => open(x)} title="내려받기" style={{ flex: 1, minWidth: 0, fontSize: T.sm, color: C.accent, fontWeight: 600, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.name}</span>
              <span style={{ fontSize: T.xs, color: C.muted }}>{fmtSize(x.size)}</span>
              <button onClick={() => { if (window.confirm(`${x.name} 을(를) 목록에서 뺄까요? 저장하면 파일도 지워집니다.`)) onChange(files.filter(y => y.s3_key !== x.s3_key)); }}
                title="빼기" style={{ border: 'none', background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 15 }}>×</button>
            </div>
          ))}
        </div>
      )}
      <div onClick={() => ref.current?.click()}
        onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        style={{
          border: `1.5px dashed ${over ? C.accent : C.borderDk}`, borderRadius: 10, background: over ? C.accentLt : C.white,
          padding: compact ? '9px 12px' : '16px 12px', textAlign: 'center', cursor: 'pointer', fontSize: T.sm, color: over ? C.accent : C.sub,
        }}>
        {busy > 0 ? `올리는 중… (${busy})` : compact ? '+ 파일 첨부 (끌어다 놓기 가능)' : '＋ 파일을 끌어다 놓거나 눌러서 올리기 · 여러 개 가능 · 20MB 이하'}
      </div>
      <input ref={ref} type="file" multiple style={{ display: 'none' }} onChange={e => add(e.target.files)} />
    </div>
  );
}
